import { clearFacet, setFacetValues } from "@/facets/filter-edit";
import type { Filter, FilterClause } from "@garimpa/lib/facetFilter";
import { pickFormatCategoryByPriority } from "@garimpa/lib/formatClassification";
import {
  FORMAT_CATEGORIES_WITH_OTHER,
  type FormatCategoryWithOther,
} from "@garimpa/lib/formatGroups";

export type WorkReleaseFormatMode = FormatCategoryWithOther | "all" | "auto";
export type WorkReleaseFormat = FormatCategoryWithOther | "all";
export type MediaCategoryCounts = Partial<Record<FormatCategoryWithOther, number>>;

const WORK_RELEASE_FORMAT_MODES = new Set<string>(["auto", "all", ...FORMAT_CATEGORIES_WITH_OTHER]);

function normalizeFilter(filter: unknown[] | Filter | undefined): Filter {
  return (filter ?? []).filter(
    (clause): clause is FilterClause => typeof clause === "object" && clause !== null,
  );
}

export function normalizeWorkReleaseFormatMode(
  value: string | null | undefined,
): WorkReleaseFormatMode {
  return value && WORK_RELEASE_FORMAT_MODES.has(value) ? (value as WorkReleaseFormatMode) : "all";
}

export function getWorkReleaseFormatMode(input: { mode?: string | null }): WorkReleaseFormatMode {
  return normalizeWorkReleaseFormatMode(input.mode);
}

export function getEffectiveWorkReleaseFormat(input: {
  mode: string | null | undefined;
  mediaCategoryCounts?: MediaCategoryCounts | null;
}): WorkReleaseFormat {
  const mode = normalizeWorkReleaseFormatMode(input.mode);
  return mode === "auto" ? pickFormatCategoryByPriority(input.mediaCategoryCounts) : mode;
}

/**
 * The `format` param a work format link writes. Choosing "all" or re-choosing the active format
 * drops the param, because a missing format is "all" and the link stays on the canonical URL.
 */
export function getWorkReleaseFormatLinkParam(input: {
  value: string;
  selectedFormat: WorkReleaseFormat;
}): string | undefined {
  return input.value === "all" || input.value === input.selectedFormat ? undefined : input.value;
}

export function getWorkReleaseFilter(input: {
  filter?: unknown[] | Filter;
  mode: string | null | undefined;
  mediaCategoryCounts?: MediaCategoryCounts | null;
}): Filter {
  const baseFilter = clearFacet(normalizeFilter(input.filter), "format");
  const format = getEffectiveWorkReleaseFormat({
    mode: input.mode,
    mediaCategoryCounts: input.mediaCategoryCounts,
  });

  return format === "all" ? baseFilter : setFacetValues(baseFilter, "format", [format]);
}

export function getWorkFormatCounts(input: {
  mediaCategoryCounts?: MediaCategoryCounts | null;
  totalCount?: number | null;
}): Record<FormatCategoryWithOther | "all", number> {
  const counts: Record<FormatCategoryWithOther | "all", number> = {
    all: input.totalCount ?? 0,
    vinyl: 0,
    cd: 0,
    cassette: 0,
    video: 0,
    other: 0,
  };

  for (const category of FORMAT_CATEGORIES_WITH_OTHER) {
    const count = Number(input.mediaCategoryCounts?.[category] ?? 0);
    counts[category] = Number.isFinite(count) ? count : 0;
  }

  return counts;
}
